import { Form, Spin } from 'antd';
import { useIsFetching } from '@tanstack/react-query';
import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { getEmptyTransformRuleGroup } from '@components/data-transform-panel/utils/initializeUtils';
import { cloneDeep, isEqual } from 'lodash';
import CustomDimension from 'emp-3.0/components/Dimensions/DimensionLegacy/CustomDimension';
import RenameDimension, {
  titleOptions,
  titlePresetMap,
  saveDimensionOptions,
} from 'emp-3.0/components/Dimensions/DimensionLegacy/RenameDimension';
import { useGetCustomDimension } from '@src/emp-3.0/api/queryHooks';
import { useGetDataTransform } from '@src/api/emp/queryHooks';
import {
  useCustomDimensionStore,
  useDataTransformPanelStore,
} from 'emp-3.0/components/Dimensions/store';
import { useDimensionPanelStore } from '@components/dimensions/store';
import { useCreatePermissions } from '@src/hooks/usePermissions';
import BuilderWrapper from 'pages/workflow/components/BuilderWrapper';
import { useWorkflowStore } from '@src/pages/workflow/store';
import { ISaveCallback } from '@src/pages/workflow/types';
import { EFormMode } from '@src/emp-3.0/types/common';
import { POST_ATTRIBUTE_TYPE_QUERY_KEY } from '@src/emp-3.0/utils/constants';
import { ALL_APPLICABLE_MAPPED_EVENTS_TYPE } from '@src/emp-3.0/constants/dimensions';
import { EAttributeType, ECategoryType } from '@src/emp-3.0/types';
import { actionTypes, TActionType } from '../../constants';
import useErrorFields from '@src/pages/workflow/hooks/useErrorFields';

import FormValidatorContext from '../../FormValidatorContext';
import { formValidator } from '@src/pages/workflow/utils';
import { DIMENSION_CONFIG, RENAME_CONFIG } from '@src/emp-3.0/constants/metrics';

const Layout = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  background-color: white;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow-x: hidden;
`;

interface IDimensionBuilderProps {
  buildType?: string;
  lastPath?: string;
}

export interface IDimensionBuilderRef {
  save: (cb?: ISaveCallback) => Promise<void>;
}

const DimensionsBuilder = forwardRef<any, IDimensionBuilderProps>(
  ({ lastPath, buildType }, ref) => {
    const hasCreatePermission = useCreatePermissions();
    const dimensionRef = useRef<any>(null);
    const isFetchingAttributeTypes =
      useIsFetching({ queryKey: [POST_ATTRIBUTE_TYPE_QUERY_KEY] }) !== 0;

    const { id } = useParams<any>();
    const hasId = id ? (id === 'new' ? false : true) : false;
    const [dimensionDraft, setDimensionDraft] = useState<any>(null);
    /**
     * currentAction tracks the currently triggered button action.
     * It's used to differentiate between the save and preview buttons,
     * since the preview button includes the save logic.
     * Possible values are 'reset', 'delete', 'save', 'preview', or null.
     */
    const [currentAction, setCurrentAction] = useState<TActionType>(null);

    const [serverSideError, setServerSideError] = useState<string>('');

    const { data: customDimensionData, isFetching: getDimensionLoading } = useGetCustomDimension({
      custom_dimension_id: Number(id),
      options: {
        enabled: buildType === 'dimension' && !!Number(id),
      },
    });
    const { data: renameDimensionData, isFetching: getRenameLoading } = useGetDataTransform(
      Number(id),
      { enabled: buildType === 'rename' && !!Number(id) },
    );

    const { errorFields, setErrorFields, resetErrorField } = useErrorFields();

    const {
      setCustomDimensionPayload,
      customDimensionPayload,
      resetCustomDimensionStore,
      createFrom,
      setCreateFrom,
      loadingStates: loadingStatesCustomDimension,
    } = useCustomDimensionStore((state) => state);
    const {
      setTransformRule,
      transformRule,
      setTransformTitle,
      setTransformId,
      loadingStates: loadingStatesDataTransform,
    } = useDataTransformPanelStore((state) => state);
    const {
      isCreateLoading: isLoadingCreateCustomDimension,
      isUpdateLoading: isLoadingUpdateCustomDimension,
      isDeleteLoading: isLoadingDeleteCustomDimension,
    } = loadingStatesCustomDimension;
    const {
      isCreateLoading: isLoadingCreateDataTransform,
      isUpdateLoading: isLoadingUpdateDataTransform,
      isDeleteLoading: isLoadingDeleteDataTransform,
    } = loadingStatesDataTransform;
    // Consolidated loading states
    const isCreateUpdateLoading = useMemo(
      () =>
        isLoadingCreateCustomDimension ||
        isLoadingUpdateCustomDimension ||
        isLoadingCreateDataTransform ||
        isLoadingUpdateDataTransform,
      [
        isLoadingCreateCustomDimension,
        isLoadingCreateDataTransform,
        isLoadingUpdateCustomDimension,
        isLoadingUpdateDataTransform,
      ],
    );
    const isDeleteLoading = useMemo(
      () => isLoadingDeleteCustomDimension || isLoadingDeleteDataTransform,
      [isLoadingDeleteCustomDimension, isLoadingDeleteDataTransform],
    );

    const [setCurPresetName] = useDimensionPanelStore((state) => [state.setCurPresetName]);

    const { setHasChanged, setFormMode, formMode } = useWorkflowStore();

    const [form] = Form.useForm();

    const handleOk = useCallback(
      async (cb?: ISaveCallback, innercb?: () => Promise<any>, nextAction?: 'preview') => {
        const values = await formValidator(
          form,
          buildType === 'rename' ? RENAME_CONFIG : DIMENSION_CONFIG,
          false,
        )
          .then(async (values) => {
            if (nextAction === 'preview') {
              dimensionRef?.current?.handleSave(cb, innercb);
            } else {
              dimensionRef?.current?.handleSave(cb);
            }
            // innercb?.();
          })
          .catch((e) => {
            console.log('create/update dimension error: ', e);
            setErrorFields(e.errorFields);
            return false;
          });
        if (!values) {
          cb?.onSettled?.();
        }
        return false;
      },
      [form, dimensionRef, setErrorFields, buildType],
    );

    const handleDelete = useCallback(async () => {
      return dimensionRef?.current?.handleDelete();
    }, [dimensionRef]);

    useImperativeHandle(
      ref,
      () => {
        return {
          save: handleOk,
        };
      },
      [handleOk],
    );

    const initDimension = (originDimensionInfo: any = {}) => {
      setCurrentAction(null);
      setHasChanged(false);
      if (buildType === 'dimension') {
        const init = {
          display_name: '',
          category: undefined,
          event_type: ALL_APPLICABLE_MAPPED_EVENTS_TYPE,
          ...originDimensionInfo,
        };
        if (formMode === EFormMode.CLONE) {
          setHasChanged(true);
          init.display_name = `${originDimensionInfo.display_name}_copy`;
          delete init.custom_dimension_id;
          delete init.id;
          setCustomDimensionPayload({
            ...init,
          });
        } else if (init.id) {
          setCustomDimensionPayload({
            ...init,
            custom_dimension_id: init.id,
          });
        } else {
          if (createFrom === 'kebab') {
            const draft = cloneDeep(customDimensionPayload);
            setDimensionDraft(draft);
            setCreateFrom('otherEntry');
          } else {
            resetCustomDimensionStore();
          }
        }
        form.setFieldsValue(init);
        setDimensionDraft(init);
      }

      if (buildType === 'rename') {
        const init = {
          transform_title: titleOptions[0].value,
          target_dimension: saveDimensionOptions[0].value,
          transform_rule: [getEmptyTransformRuleGroup()],
          ...originDimensionInfo,
          transform_name: originDimensionInfo.name || '',
        };
        if (init.target_dimension === saveDimensionOptions[1].value) {
          init.transform_title = titleOptions[1].value;
        }
        form.setFieldsValue(init);
        if (init.id) {
          setTransformId(init.id);
        } else {
          setTransformId(undefined);
        }

        setTransformRule(originDimensionInfo.transform_rule || [getEmptyTransformRuleGroup()]);
        setDimensionDraft(init);
        setTransformTitle(init.transform_title);
        setCurPresetName(titlePresetMap[init.transform_title]);
      }
    };

    const dimensionInfo = useMemo(() => {
      if (!id || id === 'new') {
        return {};
      }
      if (buildType === 'dimension') return cloneDeep(customDimensionData?.data);
      return cloneDeep((renameDimensionData as any)?.data);
    }, [customDimensionData, buildType, renameDimensionData, id]);

    useEffect(() => {
      initDimension(cloneDeep(dimensionInfo));
    }, [dimensionInfo]);

    const onValuesChange = useCallback((changedValues, allValues) => {
      if (changedValues?.transform_title) {
        initDimension({ dimensionInfo, transform_title: changedValues.transform_title });
      } else {
        setDimensionDraft((prev) => ({ ...prev, ...allValues }));
      }
      resetErrorField(changedValues);
      setServerSideError('');
    }, []);

    useEffect(() => {
      if (customDimensionPayload && buildType === 'dimension') {
        const { attribute_name, attribute_type, mapped_event_names, event_type } =
          customDimensionPayload;
        // set new values with previous values
        setDimensionDraft((prev) => ({
          ...prev,
          attribute_name,
          attribute_type,
          mapped_event_names,
          event_type,
        }));
      }
    }, [customDimensionPayload, buildType]);

    useEffect(() => {
      if (transformRule && buildType === 'rename') {
        setDimensionDraft((prev) => ({
          ...prev,
          transform_rule: transformRule,
        }));
      }
    }, [transformRule, buildType]);

    function hasEmptyArrayInRulesOrValues(arr) {
      const checkEmpty = (item) => {
        if (Array.isArray(item)) {
          if (item.length === 0) {
            return true;
          }
          return item.some(checkEmpty);
        } else if (typeof item === 'object' && item !== null) {
          return Object.values(item).some(checkEmpty);
        }
        return false;
      };
      return arr.some((obj) => checkEmpty(obj.rule));
    }

    // don't use this function temporarily, cause metric part is not ready
    const hasFillCompleted = useMemo(() => {
      if (!dimensionDraft) return false;
      if (buildType === 'dimension') {
        if (
          !dimensionDraft.display_name ||
          !dimensionDraft.category ||
          !dimensionDraft.attribute_name
        ) {
          return false;
        }
        if (
          dimensionDraft.attribute_type === 1 &&
          dimensionDraft.event_type !== ALL_APPLICABLE_MAPPED_EVENTS_TYPE &&
          dimensionDraft.mapped_event_names.length === 0
        ) {
          return false;
        }
        return true;
      } else {
        if (
          !dimensionDraft.transform_title ||
          !dimensionDraft.target_dimension ||
          !dimensionDraft.transform_name
        ) {
          return false;
        }
        if (hasEmptyArrayInRulesOrValues(dimensionDraft.transform_rule)) {
          return false;
        }
        return true;
      }
    }, [dimensionDraft, buildType]);

    const hasChanged = useMemo(() => {
      const originDimensionInfo =
        buildType === 'dimension'
          ? {
              display_name: dimensionInfo?.display_name || '',
              category: dimensionInfo?.category || ECategoryType.GLOBAL_TAGS,
              attribute_type: dimensionInfo?.attribute_type || EAttributeType.Global,
              mapped_event_names: dimensionInfo?.mapped_event_names || [],
              attribute_name: dimensionInfo?.attribute_name || '',
              event_type: dimensionInfo?.event_type || ALL_APPLICABLE_MAPPED_EVENTS_TYPE,
            }
          : {
              transform_title: titleOptions[0].value,
              target_dimension: dimensionInfo?.target_dimension || saveDimensionOptions[0].value,
              transform_name: dimensionInfo?.name || '',
              transform_rule: dimensionInfo?.transform_rule || [getEmptyTransformRuleGroup()],
            };
      if (dimensionInfo?.target_dimension === saveDimensionOptions[1].value) {
        originDimensionInfo.transform_title = titleOptions[1].value;
      }
      const draft =
        buildType === 'dimension'
          ? {
              display_name: dimensionDraft?.display_name || '',
              category: dimensionDraft?.category || ECategoryType.GLOBAL_TAGS,
              attribute_type: dimensionDraft?.attribute_type || EAttributeType.Global,
              mapped_event_names: dimensionDraft?.mapped_event_names || [],
              attribute_name: dimensionDraft?.attribute_name || '',
              event_type: dimensionDraft?.event_type || ALL_APPLICABLE_MAPPED_EVENTS_TYPE,
            }
          : {
              transform_title: dimensionDraft?.transform_title || titleOptions[0].value,
              target_dimension: dimensionDraft?.target_dimension || saveDimensionOptions[0].value,
              transform_name: dimensionDraft?.transform_name || '',
              transform_rule: dimensionDraft?.transform_rule || [getEmptyTransformRuleGroup()],
            };

      return !isEqual(originDimensionInfo, draft);
    }, [dimensionDraft, dimensionInfo, buildType]);

    const isSaveButtonDisabled = useMemo(() => {
      // remove hasFillCompleted from the condition temporarily
      // return !hasCreatePermission || !hasChanged || !hasFillCompleted;
      return !hasCreatePermission || !hasChanged || isFetchingAttributeTypes || isDeleteLoading;
    }, [hasCreatePermission, hasChanged, isFetchingAttributeTypes, isDeleteLoading]);

    useEffect(() => {
      setHasChanged(hasChanged);
    }, [hasChanged, setHasChanged]);

    useEffect(() => {
      return () => {
        resetCustomDimensionStore();
      };
    }, []);

    const ChildComponent = useCallback(() => {
      return buildType === 'rename' ? (
        <RenameDimension
          formProps={{ form: form }}
          onValuesChange={onValuesChange}
          ref={dimensionRef}
          setServerSideError={setServerSideError}
        />
      ) : (
        <CustomDimension
          formProps={{ form: form }}
          onValuesChange={onValuesChange}
          ref={dimensionRef}
          setServerSideError={setServerSideError}
        />
      );
    }, [buildType, form, onValuesChange]);

    let title = buildType === 'rename' ? 'Edit Dimension' : 'Create Dimension';
    if (id !== 'new' && formMode !== EFormMode.CLONE) {
      title = buildType === 'rename' ? 'Edit Dimension' : 'Edit Dimension';
    }
    const isGetLoading = getDimensionLoading || getRenameLoading;
    // Button states
    const disabledSaveButton = isSaveButtonDisabled;
    const disabledDeleteButton = !id || isCreateUpdateLoading;
    const errorMsg = Object.values(errorFields).some((x) => x === 'Required')
      ? '* Required fields in red are missing.'
      : serverSideError;

    return (
      <BuilderWrapper
        title={title}
        error={errorMsg}
        footer={{
          hasDeleteButton: buildType === 'rename' && id !== 'new',
          saveButtonProps: {
            loading: isCreateUpdateLoading && currentAction === actionTypes.SAVE,
            onClick: () => {
              setCurrentAction(actionTypes.SAVE);
              handleOk();
            },
            disabled:
              (disabledSaveButton || isCreateUpdateLoading) && currentAction !== actionTypes.SAVE,
          },
          deleteButtonProps: {
            usePopconfirm: true,
            confirmProps: {
              title: 'The deleted data-transform cannot be restored. Do you want to continue?',
            },
            onClick: handleDelete,
            disabled: disabledDeleteButton,
            loading: isDeleteLoading,
          },
          restButtonProps: {
            onClick: () => {
              setHasChanged(false);
              initDimension(dimensionInfo);
            },
          },
        }}
      >
        <Layout>
          <ContentWrapper>
            {isGetLoading ? (
              <Spin spinning={isGetLoading} />
            ) : (
              <FormValidatorContext.Provider value={{ enableRequired: false, errorFields }}>
                <ChildComponent />
              </FormValidatorContext.Provider>
            )}
          </ContentWrapper>
        </Layout>
      </BuilderWrapper>
    );
  },
);
DimensionsBuilder.displayName = 'DimensionsBuilder';
export default DimensionsBuilder;
